import type { DualWriteFailureClass, DualWriteOutcome } from '../Firebase/dualWrite'

/**
 * ATL-1644 — the PostHog properties `sale_created` carries so a sale missing from Supabase can be
 * explained from telemetry: WHICH sale (its Firestore id, the same id `sales.firestore_id` holds)
 * and what its dual-write did. Before this the event had neither, and nine VSH sales that never
 * reached the server were unexplainable — no device network signal, no failed request server-side.
 *
 * Flat, snake_case properties (PostHog filters on top-level keys). `dual_write_outcome` is
 * `unknown` only when no outcome was reported, which `saveNewSale` guarantees cannot happen on a
 * resolved save — so a non-zero `unknown` count is itself a defect signal, not a bucket to ignore.
 * `gate_denied` with `dual_write_pending_skip_queued: true` is NOT a delivered sale: the in-session
 * replay queue is lost on an app kill — count it as at risk until the sale is seen in Supabase.
 * `failed_lost` can over-report: an outbox enqueue that outlives its 3s race may still persist, so
 * confirm a `failed_lost` sale is absent from Supabase before counting it lost. `failed_queued` means
 * only that the outbox entry was persisted when the sale was made — NOT that it will be delivered:
 * the outbox can still drop it (attempts exhausted, 7-day expiry, a permanent refusal on replay, or
 * eviction by a later overflow), so it too is at risk until the sale is seen in Supabase.
 */
export type SaleCreatedProperties = {
  sale_id: string
  dual_write_outcome:
    | 'landed'
    | 'gate_denied'
    | 'queued_behind_document'
    | 'converted_to_master_write'
    | 'failed_queued'
    | 'failed_lost'
    | 'unknown'
  dual_write_gate?: Extract<DualWriteOutcome, { kind: 'gate-denied' }>['gate']
  dual_write_pending_skip_queued?: boolean
  dual_write_failure_class?: DualWriteFailureClass
}

export function saleCreatedProperties(
  saleId: string,
  outcome: DualWriteOutcome | undefined,
): SaleCreatedProperties {
  if (!outcome) return { sale_id: saleId, dual_write_outcome: 'unknown' }
  switch (outcome.kind) {
    case 'landed':
      return { sale_id: saleId, dual_write_outcome: 'landed' }
    case 'gate-denied':
      return {
        sale_id: saleId,
        dual_write_outcome: 'gate_denied',
        dual_write_gate: outcome.gate,
        dual_write_pending_skip_queued: outcome.pendingSkipQueued,
      }
    case 'queued-behind-document':
      return { sale_id: saleId, dual_write_outcome: 'queued_behind_document' }
    case 'converted-to-master-write':
      return { sale_id: saleId, dual_write_outcome: 'converted_to_master_write' }
    case 'failed':
      return {
        sale_id: saleId,
        dual_write_outcome: outcome.queued ? 'failed_queued' : 'failed_lost',
        dual_write_failure_class: outcome.failureClass,
      }
  }
}
